<template>
  <div class="content-with-spacing">
    <div class="dashboard-header">
      <h1>Programme Summary</h1>
    </div>
    <hr>
    <template v-if="programmes && programmes.length > 0">
      <div class="programme-summary-control-header">
        <div v-if="programmeId" class="change-programme-container">
          <span>Programme:
            <div class="programme-id-container">
              ID: <b>{{programmeId}}</b>
            </div>
          </span>
          <button class="btn btn-tertiary" @click="openChangeProg">{{programmeName || 'Choose Programme'}}</button>
          <change-programme
            v-if="programmes"
            v-model="programmeId"
            :programmes="programmes"
            @close="closeChangeProg"
            :show="showChangeProg">
          </change-programme>
          <button class="btn btn-primary copy-emails"
            v-if="programmeMemberEmailsReady && this.programmeMemberEmails.length > 0"
            @click="copyEmails">
            Copy Emails <i class="fas fa-copy" aria-hidden="true"></i>
          </button>
        </div>
      </div>
      <programme-summary-table
        v-if="programmeId"
        ref="summaryTable"
        :rows="scores"
        :programme-id="programmeId"
        :loading="!scoresReady"
      />
    </template>
  </div>
</template>

<style>
#content .dashboard-header h1 {
  margin: 0;
}
</style>

<style scoped>
.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
}

.dashboard-header + hr {
  margin-top: 0;
}

.dashboard-header button.user-search i {
  margin-right: 5px;
}

.programme-summary-control-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.change-programme-container {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 15px;
}

.change-programme-container .programme-id-container {
  font-weight: 400;
  font-size: 0.9em;
}

.change-programme-container .programme-id-container b {
  color: var(--kate-type-accent);
}

.copy-emails i {
  margin-left: 5px;
}

@media only screen and (max-width: 767px) {
  .programme-summary-control-header {
    flex-wrap: wrap;
    gap: 10px;
  }
}
</style>

<script>
import axios from 'axios';
import StorageMixin from '@mixins/storage-mixins';
import PageReadyMixin from '@mixins/page-ready-mixin';
import { copyToClipboard } from '@utils/copy-to-clipboard';
import ChangeProgramme from '../../components/change-programme.vue';
import ProgrammeSummaryTable from './programme-summary-table.vue';

const CancelToken = axios.CancelToken;

export default {
  mixins: [StorageMixin, PageReadyMixin],
  data() {
    return {
      scores: [],
      programmeMemberEmails: [],
      programmes: undefined,
      programmesReady: false,
      scoresReady: false,
      programmeMemberEmailsReady: false,
      // cancel tokens
      scoresCall: null,
      showChangeProg: false,
    };
  },
  components: {
    'programme-summary-table': ProgrammeSummaryTable,
    'change-programme': ChangeProgramme,
  },
  beforeMount() {
    this.$Loading.start();
    this.getProgrammes().then(() => {
      this.loadProgrammeId(); // changing prog ID triggers refresh
      this.getData();
      this.saveProgrammeId(this.programmeId);
    });
  },
  watch: {
    programmeId(id) {
      if (this.$route.name !== 'monitor_programme_summary') {
        // Leaving the page and programmeId param being set to undefined
        return;
      }
      if (id) {
        this.saveProgrammeId(id);
        this.getData();
      } else {
        // Load the saved programme
        this.loadProgrammeId();
      }
    },
  },
  computed: {
    programmeId: {
      get() {
        if (!this.$route.params.programmeId) {
          return undefined;
        }
        return Number(this.$route.params.programmeId);
      },
      set(id) {
        this.changeProgramme(id);
      },
    },
    ready() {
      if (this.programmesReady) {
        return (this.programmes && this.programmes.length > 0) ? this.scoresReady : true;
      }
      return false;
    },
    programmeName() {
      if (this.programmeId === undefined || this.programmes === undefined) {
        return '';
      }
      for (let i = 0; i < this.programmes.length; i++) {
        if (this.programmes[i].id === this.programmeId) {
          return this.programmes[i].name;
        }
      }
      return '';
    },
  },
  methods: {
    saveProgrammeId(id) {
      const settings = this.loadFromStorage('programme-summary') || {};
      settings.programmeId = id;
      this.dumpToStorage('programme-summary', settings);
    },
    loadProgrammeId() {
      if (this.programmeId) {
        return;
      }
      const settings = this.loadFromStorage('programme-summary');
      if (settings !== undefined && settings.programmeId) {
        this.changeProgramme(settings.programmeId);
      }
    },
    changeProgramme(id) {
      if (id === this.programmeId) {
        return;
      }
      this.$router.push({
        name: 'monitor_programme_summary',
        params: {
          programmeId: id,
        },
      });
    },
    getData() {
      this.getScores();
      this.getProgrammeMemberEmails();
    },
    getProgrammes() {
      this.programmesReady = false;
      this.$logger.info('Getting programmes for user');
      return this.$http.get('/api/curriculum/programmes?dashboard=true')
        .then(result => {
          this.$logger.info('Got programmes for user');
          this.programmes = result.data.programmes;
          if (!this.programmeId && this.programmes.length > 0) {
            this.changeProgramme(result.data.programmes[0].id);
          }
        }).catch(err => {
          this.$logger.error('Error getting programmes for user', undefined, err);
        }).then(() => {
          this.programmesReady = true;
        });
    },
    getScores() {
      if (this.programmeId === undefined) {
        return;
      }
      this.scoresReady = false;
      if (this.scoresCall) {
        this.scoresCall.cancel();
      }
      this.$logger.info('Getting scores for programme', { programmeId: this.programmeId }, true);
      this.scoresCall = CancelToken.source();
      this.$http.get(`/api/stats/programmes/${this.programmeId}/summary`, {
        cancelToken: this.scoresCall.token,
      }).then(result => {
        this.$logger.info('Got scores for programme', { programmeId: this.programmeId });
        this.scores = result.data;
      }).catch(err => {
        if (axios.isCancel(err)) {
          return;
        }
        if (err.response && err.response.status === 404) {
          this.scores = [];
          this.$logger.warn('No scores for programme', { programmeId: this.programmeId }, err);
        } else {
          this.$logger.error('Error getting scores for programme', { programmeId: this.programmeId }, err);
        }
      }).then(() => {
        this.scoresReady = true;
        if (this.$refs.summaryTable) {
          // Ref only exists if the dashboard table is actually rendered
          this.$refs.summaryTable.buildTableRows();
        }
      });
    },
    getProgrammeMemberEmails() {
      if (this.programmeId === undefined) {
        return;
      }
      this.programmeMemberEmailsReady = false;
      this.$logger.info('Getting programme member emails for programme', { programmeId: this.programmeId }, true);
      this.$http.get(`/api/curriculum/programmes/${this.programmeId}/emails`).then(result => {
        this.programmeMemberEmails = result.data.emails;
      }).catch(err => {
        this.$logger.info('Error getting programme member emails for programme', { programmeId: this.programmeId });
        this.$ktoast.showError(err);
      }).then(() => {
        this.programmeMemberEmailsReady = true;
      });
    },
    copyEmails() {
      const addressList = this.programmeMemberEmails.map(x => x.email).join(',');
      copyToClipboard(addressList);
      this.$ktoast.copy('Copied address list to clipboard', { goAway: 1500 });
    },
    openChangeProg() {
      this.showChangeProg = true;
    },
    closeChangeProg() {
      this.showChangeProg = false;
    },
  },
};
</script>
